console.log('[tables.ts loaded]');

/**
 * Airtable Table Definitions
 * 
 * Type definitions and helper functions for specific Airtable tables
 * Provides strongly-typed interfaces for table records
 */

import { getTableNameByKey } from './config';
import type { AirtableRecord } from './rest-api';

/**
 * Enum of available logical table keys (must match config)
 */
export enum TableKey {
  PROVIDERS = 'PROVIDERS',
  PRODUCTS = 'PRODUCTS',
  PROVIDER_SERVICES = 'PROVIDER_SERVICES',
  CRO_SERVICES = 'CRO_SERVICES',
  CROS = 'CROS',
  TECHNOLOGY = 'TECHNOLOGY',
  PLATFORMS = 'PLATFORMS',
}

/**
 * CRO table record types
 */
export interface CRORecord extends AirtableRecord {
  fields: {
    Name: string;
    Location?: string;
    Website?: string;
    Services?: string[];
    ContactEmail?: string;
    ContactPhone?: string;
    Notes?: string;
    StainingPlatforms?: string[];
    ImageScanning?: string[];
    ImageAnalysis?: string[];
    [key: string]: any;
  };
}

/**
 * Staining Platform table record types
 */
export interface StainingPlatformRecord extends AirtableRecord {
  fields: {
    Name: string;
    Manufacturer?: string;
    Technology?: string;
    Multiplexing?: boolean;
    MaxMarkers?: number;
    SampleTypes?: string[];
    Notes?: string;
    Companies?: string[];
    [key: string]: any;
  };
}

/**
 * Image Scanning table record types
 */
export interface ImageScanningRecord extends AirtableRecord {
  fields: {
    Name: string;
    Manufacturer?: string;
    Technology?: string;
    Resolution?: string;
    Throughput?: string;
    SupportedSlides?: string[];
    Notes?: string;
    Companies?: string[];
    [key: string]: any;
  };
}

/**
 * Image Analysis table record types
 */
export interface ImageAnalysisRecord extends AirtableRecord {
  fields: {
    Name: string;
    Developer?: string;
    AnalysisType?: string[];
    CloudBased?: boolean;
    MachineLearning?: boolean;
    BatchProcessing?: boolean;
    SupportedFormats?: string[];
    Notes?: string;
    Companies?: string[];
    [key: string]: any;
  };
}

/**
 * Company table record types
 */
export interface CompanyRecord extends AirtableRecord {
  fields: {
    Name: string;
    Website?: string;
    Location?: string;
    Category?: string[];
    Description?: string;
    StainingPlatforms?: string[];
    ImageScanning?: string[];
    ImageAnalysis?: string[];
    ContactEmail?: string;
    Notes?: string;
    [key: string]: any;
  };
}

/**
 * Platforms table record types
 */
export interface PlatformsRecord extends AirtableRecord {
  fields: {
    Name: string;
    Description?: string;
    Manufacturer?: string;
    Technology?: string;
    Companies?: string[];
    [key: string]: any;
  };
}

/**
 * Map logical keys to their record types
 */
export type TableRecordType<T extends string = string> =
  T extends 'PROVIDERS' ? CompanyRecord :
  T extends 'PRODUCTS' ? AirtableRecord :
  T extends 'PROVIDER_SERVICES' ? AirtableRecord :
  T extends 'CRO_SERVICES' ? AirtableRecord :
  T extends 'CROS' ? AirtableRecord :
  T extends 'TECHNOLOGY' ? AirtableRecord :
  T extends 'PLATFORMS' ? PlatformsRecord :
  AirtableRecord;

/**
 * Helper to get the proper table name string
 *
 * NOTE: getTableNameByKey is async. If you need a table name synchronously, use AIRTABLE_TABLES mapping from config.
 * If you need the live name, use getTableNameByKey directly and handle the Promise.
 */
// export function getTableName(key: string): string {
//   return getTableNameByKey(key); // This was incorrect: getTableNameByKey is async
// }

/**
 * Type guard functions to check record types
 */
export function isCRORecord(record: AirtableRecord): record is CRORecord {
  return 'Name' in record.fields;
}

export function isStainingPlatformRecord(record: AirtableRecord): record is StainingPlatformRecord {
  return 'Name' in record.fields && ('Manufacturer' in record.fields || 'Technology' in record.fields);
}

export function isImageScanningRecord(record: AirtableRecord): record is ImageScanningRecord {
  return 'Name' in record.fields && ('Resolution' in record.fields || 'Throughput' in record.fields);
}

export function isImageAnalysisRecord(record: AirtableRecord): record is ImageAnalysisRecord {
  return 'Name' in record.fields && ('AnalysisType' in record.fields || 'MachineLearning' in record.fields);
}

export function isCompanyRecord(record: AirtableRecord): record is CompanyRecord {
  return 'Name' in record.fields && ('Category' in record.fields || 'Description' in record.fields);
}

export type TableName = TableKey;

export type { AirtableRecord } from './rest-api'; 